<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('stockCount.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('stockCount.pageTitle'),
            active: true,
          },
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-flex ts-items-center ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <a-range-picker
              v-model="dateRange"
              :allowClear="false"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              style="width: 200px"
              @change="fetchData()"
            />
          </div>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <ts-input
              v-model="search"
              v-tooltip="$t('stockCount.search')"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
            <ts-filter ref="filter">
              <div class="row">
                <div class="col-md-12">
                  <label class="">{{ $t('employeeProfile.branch') }}</label>
                  <ts-branch-filter
                    @filter="branchFilter" 
                    class="ts-w-full"
                  ></ts-branch-filter>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table :columns="columns" :records="resources" :loading="loading">
          <template v-slot="{ record }">
            <td class="ts-whitespace-no-wrap">{{ record.branch.branch_name_en }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.sc_number }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.count_date }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.total_amount }}</td>
            <td class="ts-text-center">
              <span class="badge badge-success" v-if="record.is_adjustment"
                >Yes</span
              >
              <span class="badge badge-danger" v-else>No</span>
            </td>
            <td class="ts-whitespace-no-wrap">{{ record.remarks }}</td>
            <td class="text-left ts-space-x-3">
              <a
                href="#"
                v-tooltip="$t('edit')"
                class="text-primary"
                @click.prevent="edit(record)"
              >
                <i class="fas fa-edit"></i>
              </a>
              <a-popconfirm
                placement="leftTop"
                :title="$t('are_you_sure_to_delete')"
                :ok-text="$t('yes')"
                :cancel-text="$t('no')"
                @confirm="deleteRecord(record)"
              >
                <a href="#" :disabled="record._deleting">
                  <i
                    class="fas fa-circle-notch spin"
                    v-if="record._deleting"
                  ></i>
                  <i class="fa fa-trash text-red" v-else></i>
                </a>
              </a-popconfirm>
              <a
                v-if="!record.is_adjustment"
                href="#"
                v-tooltip="$t('stockCount.generateToAdjustment')"
                class="text-info"
                @click.prevent="generate(record)"
              >
                <i class="fas fa-cogs"></i>
              </a>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination v-model="pagination" @navigate="fetchData">
        </ts-pagination>
      </div>

      <!-- modal generate -->
      <a-modal 
        v-model="show_modal" 
        :centered="true" 
        :title="$t('generate')"
        :footer="null"
      >
        <Generate 
          :stockCountId="stockCountId"
          @cancel="onCancel" 
        />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import Generate from './components/generate'
export default {
  name: 'stockCount',
  components: {
    Generate
  },
  data() {
    return {
      loading: false,
      show_modal: false,
      stockCountId: null
    }
  },
  computed: {
    ...mapState('stock/stockCount', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.stock.stockCount.search
      },
      set(newValue) {
        this.$store.commit('stock/stockCount/SET_SEARCH', newValue)
        this.$store.commit('stock/stockCount/RESET_CURRENT_PAGE')
      },
    },
    dateRange: {
      get() {
        return this.$store.state.stock.stockCount.dateRange
      },
      set(newValue) {
        this.$store.commit('stock/stockCount/SET_DATE_RANGE', newValue)
      },
    },
    columns() {
      return [
        { name: this.$t('stockCount.branchName') },
        { name: this.$t('stockCount.stockCountNumber') },
        { name: this.$t('stockCount.date') },
        { name: this.$t('stockCount.totalAmount') },
        { name: this.$t('stockCount.isAdjust'), style: 'text-align:center' },
        { name: this.$t('stockCount.remarks') },
        {
          name: this.$t('actions'),
          style: 'text-align:left; width:7%',
        },
      ]
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    addNew() {
      this.$router.push({ name: 'stock-count-create' })
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('stock/stockCount/fetch', { ...attributes })
        .then((response) => {
          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    edit(record) {
      this.$store.commit('stock/stockCount/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'stock-count-edit',
        params: { id: record.stock_count_id },
      })
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('stock/stockCount/destroy', record.stock_count_id)
        .then((response) => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    generate(record) {
      this.show_modal = true
      this.stockCountId = record.stock_count_id
    },
    onCancel(){
      this.show_modal = false
      this.fetchData()
    },
    branchFilter(value){
      this.$store.commit('stock/stockCount/SET_BRANCH_ID', value)
      this.fetchData()
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData()
    }, 500),
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('stock/stockCount/RESET_STATE')
    next()
  },
}
</script>